import React, { useState, useEffect } from 'react';
import {
    Search,
    Settings,
    Sun,
    Moon,
    User,
    Menu,
    Zap
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';
import LoginModal from './LoginModal.tsx';
import { useIsMobile } from '../hooks/useIsMobile.ts';
import { API_URL } from '../config/api.ts';

interface TopBarProps {
    onSearch?: (term: string) => void;
    onToggleSidebar?: () => void;
    onOpenMobileMenu?: () => void;
    userData?: {
        username?: string;
        avatar?: string | null;
        subscription?: any;
        usage?: any;
        canClaim?: boolean;
    };
    onRefreshUserData?: () => void;
    onOpenProfile?: () => void;
    marginLeft?: string;
}

const TopBar: React.FC<TopBarProps> = ({ onSearch, userData: propUserData, onRefreshUserData, onOpenProfile, onOpenMobileMenu, marginLeft = '0' }) => {
    const { isDarkMode, toggleTheme } = useTheme();
    const isMobile = useIsMobile();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [username, setUsername] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [userData, setUserData] = useState<any>(null);
    const [userAvatar, setUserAvatar] = useState<string | null>(null);
    const [showClaimPopup, setShowClaimPopup] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);

    const handleLogout = async () => {
        try {
            const response = await fetch(`${API_URL}/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                setIsLoggedIn(false);
                setUsername('');
                setUserAvatar(null);
                setUserData(null);
                setShowUserMenu(false);
                window.location.reload(); // Reload to refresh state properly
            }
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleClaimDaily = async () => {
        console.log('🎁 handleClaimDaily called');
        try {
            const response = await fetch(`${API_URL}/user/claim-daily`, {
                method: 'POST',
                credentials: 'include'
            });
            const data = await response.json();
            console.log('🎁 Claim response:', data);
            if (data.success) {
                setShowClaimPopup(false);
                onRefreshUserData?.();
                console.log('✅ Daily messages claimed successfully');
            } else {
                console.error('❌ Claim failed:', data.message);
                alert(data.message || 'Failed to claim daily messages');
            }
        } catch (error) {
            console.error('❌ Error claiming daily messages:', error);
            alert('Error claiming daily messages. Please try again.');
        }
    };

    // Show claim popup when canClaim becomes true
    useEffect(() => {
        if (propUserData?.canClaim) {
            setShowClaimPopup(true);
        }
    }, [propUserData?.canClaim]);

    // Use prop userData if provided
    useEffect(() => {
        if (propUserData) {
            if (propUserData.username) {
                setIsLoggedIn(true);
                setUsername(propUserData.username);
            }
            if (propUserData.avatar) {
                setUserAvatar(propUserData.avatar);
            }
            setUserData((prev: any) => ({ ...prev, ...propUserData }));
        }
    }, [propUserData]);

    // Fallback: fetch user data if no prop provided
    useEffect(() => {
        if (propUserData && propUserData.username) return; // Skip if we have prop data with username

        const checkSession = async () => {
            try {
                const response = await fetch(`${API_URL}/user`, {
                    credentials: 'include'
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.user) {
                        setIsLoggedIn(true);
                        setUsername(data.user.username);
                        setUserAvatar(data.user.avatar);
                        setUserData(data.user);
                    }
                }
            } catch (error) {
                console.error('Error checking session:', error);
            }
        };

        checkSession();
    }, [propUserData]);

    return (
        <div style={{
            height: isMobile ? '60px' : '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 40px',
            backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#f0f0f0'}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            marginLeft: isMobile ? '0' : marginLeft,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            {/* Left Section */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '20px' }}>
                {/* Hamburger Menu - Mobile Only */}
                {isMobile && (
                    <button
                        onClick={onOpenMobileMenu}
                        aria-label="Open navigation menu"
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: isDarkMode ? 'white' : '#333'
                        }}
                    >
                        <Menu size={22} />
                    </button>
                )}

                {/* Logo - Mobile */}
                {isMobile && (
                    <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>
                        EcstasyAI
                    </div>
                )}

                {/* Search Bar - Desktop or Expanded Mobile */}
                {(!isMobile || showMobileSearch) && (
                    <div style={{
                        position: isMobile && showMobileSearch ? 'fixed' : 'relative',
                        width: isMobile ? '100%' : '500px',
                        ...(isMobile && showMobileSearch && {
                            top: '60px',
                            left: 0,
                            right: 0,
                            padding: '12px 16px',
                            backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff',
                            zIndex: 100,
                            borderBottom: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#f0f0f0'}`,
                            boxSizing: 'border-box'
                        })
                    }}>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <Search
                                size={18}
                                color={isDarkMode ? '#94a3b8' : '#94a3b8'}
                                style={{
                                    position: 'absolute',
                                    left: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none'
                                }}
                            />
                            <input
                                type="text"
                                onChange={(e) => onSearch?.(e.target.value)}
                                placeholder="Search characters..."
                                style={{
                                    width: '100%',
                                    height: '44px',
                                    padding: '0 16px 0 44px',
                                    borderRadius: '22px',
                                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                    fontSize: '15px',
                                    outline: 'none',
                                    color: isDarkMode ? '#e2e8f0' : '#1e293b',
                                    transition: 'all 0.3s ease',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Right Actions */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
            }}>

                {/* Search Button - Mobile Only */}
                {isMobile && (
                    <button
                        onClick={() => setShowMobileSearch(!showMobileSearch)}
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: showMobileSearch
                                ? 'rgba(168, 85, 247, 0.2)'
                                : (isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9'),
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: showMobileSearch ? '#a855f7' : (isDarkMode ? 'white' : '#333'),
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Search size={20} />
                    </button>
                )}

                {/* Theme Toggle */}
                <div
                    onClick={toggleTheme}
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: isDarkMode ? '#fbbf24' : '#a855f7',
                        transition: 'all 0.3s ease',
                        marginRight: '8px'
                    }}
                >
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </div>

                {/* Social Icons */}
                {/* Social Icons - Desktop only */}
                {!isMobile && (
                    <div style={{ display: 'flex', gap: '8px', marginRight: '16px' }}>
                        {/* Subscribestar */}
                        <SocialIcon
                            icon={<svg width="20" height="20" viewBox="0 0 936.86 936.86" fill="currentColor"><path d="M479.11 936.74c-3.55.08-7.11.12-10.68.12C209.72 936.86 0 727.14 0 468.43 0 280.08 111.16 117.7 271.45 43.31c21.94 40.07 49.93 72.72 91.53 110.12 21.25 19.1 47.21 38.95 75.34 62.67 45.67 38.51 71.7 69.35 86.24 95.59-17.1-3.2-35.76-4.93-56.13-4.93-27.71 0-52.3 3.11-74.11 8.69v-26.46H237.43v91.11c0 9.44-1.21 41.78 6.45 80.5-5.31 15.91-9.07 31.74-11.7 46.78-4.12 23.57-5.42 45.18-5.42 62.05 0 79.11 22.23 164.89 123.56 253.78 101.17 88.75 107.98 87.3 128.79 113.53z" /><path d="M936.86 468.43c0 188.38-111.2 350.79-271.54 425.15-.53-1.03-35.3-66.92-128.78-136.15-56.72-42-95.72-79.01-117.77-120.92-.51-.97-1.02-1.95-1.51-2.93 15.66 2.51 32.67 3.85 51.17 3.85 22.75 0 43.39-1.97 62.11-5.57 5.46-1.04 10.75-2.23 15.89-3.54v22.11H708.1v-87c0-19.08-2.14-47.96-10.06-79.55 15.16-45.74 15.72-86.84 15.72-98.45 0-29.67 5.33-80-33.67-153.67-39-73.66-101-123.33-151-162.33S454.42.21 454.42.21c4.65-.14 9.32-.21 14.01-.21 258.7 0 468.43 209.72 468.43 468.43z" /></svg>}
                            isDarkMode={isDarkMode}
                            href="https://subscribestar.adult/juanpiamvs"
                            ariaLabel="Follow on SubscribeStar"
                        />
                        {/* Discord */}
                        <SocialIcon
                            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" /></svg>}
                            isDarkMode={isDarkMode}
                            href="https://discord.gg/Wth2t6NSWG"
                            ariaLabel="Join Discord Server"
                        />
                        {/* X (Twitter) */}
                        <SocialIcon
                            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>}
                            isDarkMode={isDarkMode}
                            href="https://x.com/JuanpiAMVs"
                            ariaLabel="Follow on X (Twitter)"
                        />
                    </div>
                )}

                {/* Message Counter - Before Settings - Desktop Only */}
                {!isMobile && isLoggedIn && (
                    <div
                        onClick={() => propUserData?.canClaim && handleClaimDaily()}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            backgroundColor: propUserData?.canClaim
                                ? 'rgba(34, 197, 94, 0.15)'
                                : (isDarkMode ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.08)'),
                            border: `1px solid ${propUserData?.canClaim ? 'rgba(34, 197, 94, 0.4)' : 'rgba(168, 85, 247, 0.2)'}`,
                            borderRadius: '20px',
                            cursor: propUserData?.canClaim ? 'pointer' : 'default',
                            position: 'relative',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <span style={{ fontSize: '14px' }}>💬</span>
                        <span style={{
                            fontWeight: '600',
                            fontSize: '13px',
                            color: isDarkMode ? '#e2e8f0' : '#334155'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {/* Daily Messages */}
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>{Math.max(0, (propUserData?.usage?.messageLimit || 50) - (propUserData?.usage?.messages || 0))}</span>
                                </span>

                                {/* Boosters - Lightning Icon */}
                                {(propUserData?.usage?.boosterMessages || 0) > 0 && (
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '2px',
                                        color: '#eab308', // Yellow-500
                                        backgroundColor: isDarkMode ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.1)',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        fontSize: '12px'
                                    }} title="Booster Messages (Never expire)">
                                        <Zap size={12} fill="currentColor" />
                                        <span>{propUserData?.usage?.boosterMessages}</span>
                                    </span>
                                )}
                            </span>
                        </span>

                        {/* Claim Popup */}
                        {showClaimPopup && propUserData?.canClaim && (
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                right: 0,
                                backgroundColor: isDarkMode ? '#1e1e2e' : 'white',
                                border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.4)'}`,
                                borderRadius: '12px',
                                padding: '12px 16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                zIndex: 1000,
                                whiteSpace: 'nowrap',
                                animation: 'pulse 2s infinite'
                            }}>
                                <div style={{
                                    fontWeight: '600',
                                    color: '#22c55e',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <span>🎁</span>
                                    <span>Claim your daily messages!</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Settings button - Desktop only */}
                {!isMobile && (
                    <div
                        onClick={onOpenProfile}
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#a855f7',
                            transition: 'background-color 0.3s'
                        }}
                        title="Edit Profile"
                    >
                        <Settings size={20} />
                    </div>
                )}

                {/* User profile - Desktop only */}
                {!isMobile && isLoggedIn ? (
                    <div style={{ position: 'relative' }}>
                        <div
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '4px',
                                paddingRight: '16px',
                                borderRadius: '24px',
                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}
                        >
                            <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                overflow: 'hidden'
                            }}>
                                {userAvatar ? (
                                    <img src={userAvatar} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                                ) : (
                                    <User size={18} />
                                )}
                            </div>
                            <span style={{
                                fontWeight: '600',
                                fontSize: '14px',
                                color: isDarkMode ? '#e2e8f0' : '#1e293b'
                            }}>
                                {username}
                            </span>
                        </div>

                        {/* Dropdown Menu */}
                        {showUserMenu && (
                            <div style={{
                                position: 'absolute',
                                top: '120%',
                                right: 0,
                                width: '280px',
                                backgroundColor: isDarkMode ? '#1e293b' : 'white',
                                borderRadius: '24px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                padding: '24px',
                                zIndex: 100,
                                border: `2px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : '#f3e8ff'}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px'
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        letterSpacing: '1px',
                                        color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                                        marginBottom: '4px',
                                        textTransform: 'uppercase'
                                    }}>
                                        Active Plan
                                    </div>
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: '800',
                                        color: '#a855f7',
                                        textShadow: '0 2px 10px rgba(168, 85, 247, 0.2)'
                                    }}>
                                        {userData?.subscription?.tier || 'Free'}
                                    </div>
                                </div>

                                <div style={{
                                    width: '100%',
                                    backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : '#f3e8ff',
                                    borderRadius: '16px',
                                    padding: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}>
                                    <UsageRow
                                        label="Messages"
                                        current={propUserData?.usage?.messagesAvailable ?? ((propUserData?.usage?.messageLimit || 50) - (propUserData?.usage?.messages || 0))}
                                        max={propUserData?.usage?.messageLimit || 50}
                                        isDarkMode={isDarkMode}
                                    />
                                </div>

                                <div style={{
                                    width: '100%',
                                    height: '1px',
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                                    margin: '4px 0'
                                }} />

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    width: '100%'
                                }}>


                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            width: '100%',
                                            padding: '8px',
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '14px',
                                            textAlign: 'left'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    >
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    !isMobile && (
                        <button
                            onClick={() => setShowLoginModal(true)}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#a855f7',
                                color: 'white',
                                border: 'none',
                                borderRadius: '24px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '14px',
                                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)',
                                transition: 'transform 0.2s'
                            }}
                        >
                            Sign In
                        </button>
                    )
                )}
            </div>

            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
            />
        </div >
    );
};

interface UsageRowProps {
    label: string;
    current: number;
    max: number;
    isDarkMode: boolean;
}

const UsageRow: React.FC<UsageRowProps> = ({ label, current, max, isDarkMode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: isDarkMode ? '#a855f7' : '#7e22ce'
        }}>
            {label}:
        </span>
        <span style={{
            fontSize: '14px',
            color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#64748b',
            fontFamily: 'monospace'
        }}>
            {current}
        </span>
    </div>
);

const SocialIcon = ({ icon, isDarkMode, href, ariaLabel }: { icon: React.ReactNode, isDarkMode: boolean, href?: string, ariaLabel: string }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        title={ariaLabel}
        style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : '#f3e8ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#a855f7',
            transition: 'all 0.3s ease',
            textDecoration: 'none'
        }}
    >
        {icon}
    </a>
);

export default TopBar;


